import axios from 'axios';
import {HybridAuthApiModule} from './HybridAuthApiModule';

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v';
const authModule = new HybridAuthApiModule();

/**
 * Setup axios interceptors so every API call carries the signed-in user's token:
 * a current Firebase ID token when Firebase auth is on (they expire hourly, so
 * the saved copy can't be reused), otherwise the legacy JWT saved at login.
 */
export const setupAxiosInterceptors = () => {
  // Request interceptor - Add token to all requests
  axios.interceptors.request.use(
    async (config) => {
      const firebaseToken = await authModule.getIdToken().catch(() => undefined);
      if (firebaseToken) {
        config.headers.Authorization = `Bearer ${firebaseToken}`;
        return config;
      }

      const lsValue = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);
      if (lsValue) {
        try {
          const auth = JSON.parse(lsValue);
          if (auth.api_token) {
            config.headers.Authorization = `Bearer ${auth.api_token}`;
          }
        } catch (error) {
          console.error('Error parsing auth from localStorage:', error);
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - Handle token expiration
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Token rejected by backend: 401 (standard unauthorized) or 601
      // (backend's CODE.RESTRICTED — used for "Invalid or expired token").
      // Sign out, clear local state, and full-reload to /auth/login so any
      // module-level state in the SPA is reset cleanly.
      const status = error.response?.status;
      if ((status === 401 || status === 601) && !originalRequest._retry) {
        originalRequest._retry = true;

        await authModule.logout().catch(() => undefined);

        localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
        localStorage.removeItem('current-user');

        window.location.href = '/auth/login';

        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );
};
